// API クライアント

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import {
  APIResponse,
  APIError,
  getApiConfig,
  UploadProgressEvent,
  AIQuotaCheckResponse,
} from '../types';
import type { ExtractedCompanyInfo, UploadData } from '../types/bcp-generation';

export interface AIConsultRequest {
  question: string;
  bcp_id?: string;
  session_id?: string;
  tenant_id?: string;
  current_section?: string;
  current_field?: string;
  form_data?: Record<string, string>;
  bcp_type?: string;
}

export interface AIConsultReference {
  text: string;
  source: string;
  score: string;
}

export interface AIConsultResponse {
  success: boolean;
  answer: string;
  references?: AIConsultReference[];
  context_used?: Record<string, string>;
  error?: string;
}

class APIClient {
  private client: AxiosInstance;
  private csrfToken: string | null = null;

  constructor() {
    // Get config dynamically to ensure env vars are evaluated at runtime
    const config = getApiConfig();

    // Log the API base URL for debugging
    console.log('API Base URL:', config.baseURL);

    this.client = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout,
      headers: config.headers,
    });

    this.setupInterceptors();

    // Initialize CSRF token asynchronously
    void this.initCsrfToken();
  }

  private async initCsrfToken(): Promise<void> {
    try {
      const response = await this.client.get<{ csrf_token: string }>('/auth/csrf-token');
      this.csrfToken = response.data.csrf_token;
      console.log('CSRF token initialized');
    } catch (error) {
      console.warn('Failed to initialize CSRF token:', error);
      // Don't throw error - CSRF token will be fetched on demand
    }
  }

  async refreshCsrfToken(): Promise<string> {
    try {
      const response = await this.client.get<{ csrf_token: string }>('/auth/csrf-token');
      this.csrfToken = response.data.csrf_token;
      console.log('CSRF token refreshed');
      return this.csrfToken;
    } catch (error) {
      console.error('Failed to refresh CSRF token:', error);
      this.csrfToken = null;
      throw error;
    }
  }

  private setupInterceptors() {
    // リクエストインターセプター
    this.client.interceptors.request.use(
      async (config) => {
        // リクエスト前の処理（認証トークン付与など）
        console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
        console.log(`Request baseURL: ${config.baseURL}`);
        console.log(`Request full URL: ${config.baseURL}${config.url}`);
        console.log(`Request headers:`, config.headers);

        // Add CSRF token to non-GET requests
        const method = config.method?.toUpperCase();
        if (method && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
          // If CSRF token is not available, try to fetch it
          if (!this.csrfToken) {
            console.warn('CSRF token not available, fetching now...');
            try {
              await this.refreshCsrfToken();
            } catch (error) {
              console.error('Failed to fetch CSRF token:', error);
              // Continue without token - will likely fail with 403
            }
          }

          if (this.csrfToken) {
            config.headers['X-CSRF-Token'] = this.csrfToken;
            console.log('CSRF token added to request');
          } else {
            console.warn('CSRF token still not available for request');
          }
        }

        return config;
      },
      (error: unknown) => {
        console.error('Request Error:', error);
        return Promise.reject(error);
      }
    );

    // レスポンスインターセプター
    this.client.interceptors.response.use(
      (response: AxiosResponse<APIResponse<unknown>>) => {
        console.log(`API Response: ${response.status} ${response.config.url}`);
        console.log(`Response Content-Type: ${response.headers['content-type']}`);
        console.log(`Response data type: ${typeof response.data}`);
        console.log(`Response data preview:`, JSON.stringify(response.data)?.substring(0, 200));
        return response;
      },
      (error: unknown) => {
        console.error('Response Error:', error);

        const axiosError = axios.isAxiosError<{
          detail?: unknown;
          message?: unknown;
        }>(error)
          ? error
          : null;
        const responseData = axiosError?.response?.data;
        const detail = responseData?.detail;

        // Handle AI quota exceeded errors (429)
        if (axiosError?.response?.status === 429) {
          console.log('429 error detected, response data:', responseData);
          console.log('Detail object:', detail);
          // Dispatch custom event for quota exceeded
          if (detail && typeof detail === 'object') {
            const quotaDetail = detail as Record<string, unknown>;
            // Check if this is a quota limit error (not just rate limiting)
            if (
              quotaDetail.accepted_tokens !== undefined ||
              (typeof quotaDetail.message === 'string' &&
                quotaDetail.message.includes('クォータ'))
            ) {
              console.log('Dispatching ai-quota-exceeded event');
              const event = new CustomEvent('ai-quota-exceeded', {
                detail: {
                  quotaWarning: detail,
                  response: responseData,
                },
              });
              window.dispatchEvent(event);
              console.log('ai-quota-exceeded event dispatched');
            } else {
              console.log('Quota condition not met, event not dispatched');
            }
          } else {
            console.log('Detail is not an object, event not dispatched');
          }
        }

        // Handle authentication errors (401) and authorization errors (403)
        if (axiosError?.response?.status === 401 || axiosError?.response?.status === 403) {
          // 開発環境または認証が無効の場合はリダイレクトしない
          const isDevelopment = import.meta.env.DEV;
          const authEnabled = import.meta.env.VITE_ENABLE_AUTHENTICATION === 'true';
          const currentPath = window.location.pathname;

          // エラーページにいる場合はリダイレクトしない
          const isErrorPage = currentPath === '/error';

          if (isDevelopment || !authEnabled || isErrorPage) {
            console.warn('Session invalid or expired (auth disabled in development or on error page)');
          } else {
            console.warn('Session invalid or expired, redirecting to login...');
            // Import getLoginRedirectUrl dynamically to avoid circular dependencies
            const rawToshoMyPageUrl: unknown = import.meta.env.VITE_TOSHO_MYPAGE_URL;
            const toshoMyPageUrl =
              (typeof rawToshoMyPageUrl === 'string' && rawToshoMyPageUrl) ||
              'https://01.tokyocci-test.prod.powercms.cloud';
            const baseUrl = window.location.origin;
            const redirectUrl = `${baseUrl}/auth/start?redirect=${currentPath}`;
            const encodedUrl = encodeURIComponent(redirectUrl);
            window.location.href = `${toshoMyPageUrl}/?logout=1&bcp_redirect_url=${encodedUrl}`;
          }
        }

        const apiError: APIError = {
          code: axiosError?.response?.status?.toString() || 'UNKNOWN',
          message:
            (typeof responseData?.message === 'string' ? responseData.message : undefined) ||
            (error instanceof Error ? error.message : undefined) ||
            'An unknown error occurred',
          details: responseData,
          timestamp: new Date().toISOString(),
        };

        return Promise.reject(apiError);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    const response = await this.client.get<APIResponse<T>>(url, config);
    return response.data;
  }

  async post<TResponse, TPayload = unknown>(
    url: string,
    data?: TPayload,
    config?: AxiosRequestConfig<TPayload>
  ): Promise<APIResponse<TResponse>> {
    const response = await this.client.post<
      APIResponse<TResponse>,
      AxiosResponse<APIResponse<TResponse>>,
      TPayload
    >(url, data, config);
    return response.data;
  }

  async put<TResponse, TPayload = unknown>(
    url: string,
    data?: TPayload,
    config?: AxiosRequestConfig<TPayload>
  ): Promise<APIResponse<TResponse>> {
    const response = await this.client.put<
      APIResponse<TResponse>,
      AxiosResponse<APIResponse<TResponse>>,
      TPayload
    >(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    const response = await this.client.delete<APIResponse<T>>(url, config);
    return response.data;
  }

  async getRaw<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    // Return raw Axios response (includes headers, status, etc.)
    return await this.client.get<T>(url, config);
  }

  async postRaw<TResponse, TPayload = unknown>(
    url: string,
    data?: TPayload,
    config?: AxiosRequestConfig<TPayload>
  ): Promise<AxiosResponse<TResponse>> {
    // Return raw Axios response (includes headers, status, etc.)
    return await this.client.post<TResponse, AxiosResponse<TResponse>, TPayload>(
      url,
      data,
      config
    );
  }

  async uploadFiles(
    files: File[],
    onProgress?: (event: UploadProgressEvent) => void
  ): Promise<APIResponse<UploadData>> {
    if (files.length === 0) {
      throw new Error('No files provided');
    }

    // Upload multiple files sequentially (not in parallel)
    const results = [];
    let totalUploaded = 0;
    const totalSize = files.reduce((sum, f) => sum + f.size, 0);
    let sessionId: string | null = null;
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      
      // Add session_id if we have one from previous upload
      if (sessionId) {
        formData.append('session_id', sessionId);
      }

      const config: AxiosRequestConfig = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            // Calculate overall progress across all files
            const currentFileProgress = progressEvent.loaded;
            const overallProgress = totalUploaded + currentFileProgress;
            const percentage = Math.round((overallProgress * 100) / totalSize);
            
            onProgress({
              loaded: overallProgress,
              total: totalSize,
              percentage,
            });
          }
        },
      };

      try {
        console.log(`Uploading file ${i + 1}/${files.length}: ${file.name}`);
        const result = await this.post<UploadData, FormData>('/api/upload-bcp', formData, config);
        results.push(result);
        totalUploaded += file.size;
        
        // Save session_id from first upload for subsequent files
        if (!sessionId && result.data?.session_id) {
          sessionId = result.data.session_id;
          console.log('Using session ID:', sessionId);
        }
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        throw error;
      }
    }
    
    // Merge all extracted info from all files
    const mergedClients: string[] = [];
    const mergedSuppliers: string[] = [];
    const mergedExtractedInfo: ExtractedCompanyInfo = {
      company_name: null,
      address: null,
      tel: null,
      fax: null,
      employees: null,
      business_content: null,
      main_business: null,
      clients: mergedClients,
      suppliers: mergedSuppliers
    };
    
    // Merge extracted info from all uploaded files
    for (const result of results) {
      if (result.data?.extracted_info) {
        const info = result.data.extracted_info;
        
        // Use the first non-null company_name found
        if (!mergedExtractedInfo.company_name && info.company_name) {
          mergedExtractedInfo.company_name = info.company_name;
        }
        
        // Use the first non-null address found
        if (!mergedExtractedInfo.address && info.address) {
          mergedExtractedInfo.address = info.address;
        }
        
        // Use the first non-null tel found
        if (!mergedExtractedInfo.tel && info.tel) {
          mergedExtractedInfo.tel = info.tel;
        }
        
        // Use the first non-null fax found
        if (!mergedExtractedInfo.fax && info.fax) {
          mergedExtractedInfo.fax = info.fax;
        }

        // Use the first non-null employees found
        if (!mergedExtractedInfo.employees && info.employees) {
          mergedExtractedInfo.employees = info.employees;
        }

        // Use the first non-null business_content found
        if (!mergedExtractedInfo.business_content && info.business_content) {
          mergedExtractedInfo.business_content = info.business_content;
        }

        // Use the first non-null main_business found
        if (!mergedExtractedInfo.main_business && info.main_business) {
          mergedExtractedInfo.main_business = info.main_business;
        }

        // Merge clients (avoid duplicates)
        if (info.clients && Array.isArray(info.clients)) {
          for (const client of info.clients) {
            if (!mergedClients.includes(client)) {
              mergedClients.push(client);
            }
          }
        }
        
        // Merge suppliers (avoid duplicates)
        if (info.suppliers && Array.isArray(info.suppliers)) {
          for (const supplier of info.suppliers) {
            if (!mergedSuppliers.includes(supplier)) {
              mergedSuppliers.push(supplier);
            }
          }
        }
      }
    }
    
    // Return the last result which should contain backend's merged info
    const finalResult = results[results.length - 1];
    if (finalResult.data) {
      // Use backend's merged info if available, otherwise use our client-side merge
      if (finalResult.data.merged_extracted_info) {
        finalResult.data.extracted_info = finalResult.data.merged_extracted_info;
        console.log('Using backend merged info:', finalResult.data.merged_extracted_info);
      } else {
        finalResult.data.extracted_info = mergedExtractedInfo;
        console.log('Using client-side merged info:', mergedExtractedInfo);
      }
      finalResult.data.all_upload_results = results; // Keep all results for reference
    }
    
    return finalResult;
  }

  async createBcpGenerationJob(data: any): Promise<{ job_id: string }> {
    // POST /api/bcp/generate-async はジョブ作成のみで即応答する
    const response = await this.client.post<{ job_id: string }>(
      '/api/bcp/generate-async',
      data,
      undefined
    );
    return response.data;
  }

  async getBcpGenerationJob(jobId: string): Promise<{
    job_id: string;
    status: 'pending' | 'processing' | 'completed' | 'failed';
    result?: any;
    error?: string | null;
  }> {
    const response = await this.client.get(`/api/bcp/jobs/${jobId}`, undefined);
    return response.data;
  }

  async generateBCP(
    data: any,
    options?: { pollIntervalMs?: number; maxPollTimeMs?: number }
  ): Promise<any> {
    // 非同期ジョブ + ポーリング方式。
    // 生成は60秒以上かかることがあり、同期 /api/bcp/generate は
    // CloudFront/ALB の60秒タイムアウトで応答が届かないため使用しない。
    // 戻り値は同期版レスポンスと同一スキーマ (bcp_document 等) の dict。
    const pollIntervalMs = options?.pollIntervalMs ?? 3000;
    const maxPollTimeMs = options?.maxPollTimeMs ?? 600000; // 10分

    const { job_id } = await this.createBcpGenerationJob(data);

    // 壁時計ではなく試行回数で上限管理する (フェイクタイマー下でも決定的)
    const maxAttempts = Math.max(1, Math.ceil(maxPollTimeMs / pollIntervalMs));
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      // 超入門など数秒で終わる生成の体感を良くするため初回は1秒で確認
      const waitMs = attempt === 0 ? Math.min(1000, pollIntervalMs) : pollIntervalMs;
      await new Promise((resolve) => setTimeout(resolve, waitMs));

      const job = await this.getBcpGenerationJob(job_id);

      if (job.status === 'completed') {
        return job.result;
      }
      if (job.status === 'failed') {
        throw this.toBcpGenerationError(job.error);
      }
    }

    const timeoutError: APIError = {
      code: '504',
      message: 'BCP生成がタイムアウトしました。時間をおいて再度お試しください。',
      timestamp: new Date().toISOString(),
    };
    throw timeoutError;
  }

  private toBcpGenerationError(error?: string | null): APIError {
    // ワーカーは HTTPException を {"status_code": N, "detail": {...}} の
    // JSON文字列で保存する。パースできればコード/詳細を復元する。
    let code = '500';
    let message = error || 'BCP生成に失敗しました';
    let details: any;

    if (error) {
      try {
        const parsed = JSON.parse(error);
        if (parsed && typeof parsed === 'object' && parsed.status_code) {
          code = String(parsed.status_code);
          details = parsed.detail;
          if (parsed.detail && typeof parsed.detail === 'object') {
            message = parsed.detail.message || message;
            if (code === '429') {
              // 同期版 (axios インターセプター) と同じクォータ超過イベントを
              // 発火してモーダル表示を維持する
              window.dispatchEvent(
                new CustomEvent('ai-quota-exceeded', {
                  detail: { quotaWarning: parsed.detail, response: parsed },
                })
              );
            }
          } else if (parsed.detail != null) {
            message = String(parsed.detail);
          }
        }
      } catch {
        // JSONでなければ文字列のまま扱う
      }
    }

    return {
      code,
      message,
      details,
      timestamp: new Date().toISOString(),
    };
  }

  async exportBCP<TPayload extends object>(
    data: TPayload,
    format: string
  ): Promise<APIResponse<unknown>> {
    return this.post<unknown, TPayload & { format: string }>(
      '/api/bcp/export',
      { ...data, format }
    );
  }

  async healthCheck(): Promise<APIResponse<unknown>> {
    return this.get('/api/health');
  }

  async consultAI(request: AIConsultRequest): Promise<AIConsultResponse> {
    const response = await this.client.post<AIConsultResponse>(
      '/api/ai-consult',
      request
    );
    return response.data;
  }

  async extractCompanyInfoFromUrl(url: string): Promise<ExtractFromUrlResponse> {
    const response = await this.client.post<ExtractFromUrlResponse>(
      '/api/extract-from-url',
      { url }
    );

    // Normalize null to empty string for optional fields
    if (response.data.data?.employee_count === null) {
      response.data.data.employee_count = '';
    }

    return response.data;
  }

  async generateStockpile(data: {
    company_info?: string;
    employees?: string;
    business_type?: string;
    location?: string;
    critical_business?: string;
  }): Promise<{ stockpile_plan: string }> {
    const response = await this.client.post<{ stockpile_plan: string }>(
      '/api/bcp/generate-stockpile',
      data
    );
    return response.data;
  }

  async submitInquiry(data: InquiryRequest): Promise<InquiryResponse> {
    try {
      const response = await this.client.post<InquiryResponse>(
        '/api/inquiry',
        data
      );
      return response.data;
    } catch (error) {
      console.error('Inquiry API error:', error);
      // Return error response
      return {
        success: false,
        error: 'ネットワークエラーが発生しました。もう一度お試しください。',
      };
    }
  }

  async checkAIQuota(): Promise<AIQuotaCheckResponse> {
    const response = await this.client.get<AIQuotaCheckResponse>(
      '/api/ai/quota/check'
    );
    return response.data;
  }
}

// Export the class for direct use
export { APIClient };

// シングルトンインスタンス
export const apiClient = new APIClient();

// 便利な関数をエクスポート
export const uploadFiles = (files: File[], onProgress?: (event: UploadProgressEvent) => void) =>
  apiClient.uploadFiles(files, onProgress);

export const generateBCP = (
  data: any,
  options?: { pollIntervalMs?: number; maxPollTimeMs?: number }
) => apiClient.generateBCP(data, options);

export const exportBCP = <TPayload extends object>(data: TPayload, format: string) =>
  apiClient.exportBCP(data, format);

export const healthCheck = () =>
  apiClient.healthCheck();

export const consultAI = (request: AIConsultRequest) =>
  apiClient.consultAI(request);

export interface ExtractFromUrlRequest {
  url: string;
}

export interface ExtractFromUrlResponse {
  success: boolean;
  data?: {
    company_name: string;
    location: string;
    employee_count: string | null;
    business_description: string;
  };
  error?: string;
  message?: string;
}

export const extractCompanyInfoFromUrl = (url: string) =>
  apiClient.extractCompanyInfoFromUrl(url);

export const generateStockpile = (data: {
  company_info?: string;
  employees?: string;
  business_type?: string;
  location?: string;
  critical_business?: string;
}) => apiClient.generateStockpile(data);

export interface InquiryRequest {
  name: string;
  company: string;
  email: string;
  message: string;
}

export interface InquiryResponse {
  success: boolean;
  message?: string;
  error?: string;
}

export const submitInquiry = (data: InquiryRequest) =>
  apiClient.submitInquiry(data);

export const checkAIQuota = () =>
  apiClient.checkAIQuota();
